import filter from 'lodash/filter';
import find from 'lodash/find';
import every from 'lodash/every';
import some from 'lodash/some';
import type { TranslationKey } from '../i18n/i18n';
import { containsConceptByKey } from './Concepts';
import type { Concept, ConceptKey, Concepts } from './Concepts';
import type { Points, Score } from './Score';
import combinations from './combinations.json';

export type CombinationKey =
	| 'parkingCarReduced_parkingBikeIncreased'
	| 'parkingCarReduced_mobilityStations'
	| 'speedlimit20_bikeSwarm'
	| string;

export interface Texts {
	readonly reason: TranslationKey;
}

export interface Combination {
	readonly name: CombinationKey;
	readonly concepts: ConceptKey[];
	readonly directional: boolean;
	readonly bonusPoints: Points;
	readonly texts: Texts;
}

export type Combinations = Combination[];

export const getCombinations = (): Combinations => combinations;

export const combinationContainsConcept = (
	combination: Combination,
	concept: Concept
): boolean => {
	return !!find(
		combination.concepts,
		(conceptKey: ConceptKey) => conceptKey === concept.name
	);
};

export const matchesConcepts = (
	combination: Combination,
	concepts: Concepts
): boolean => {
	return every(combination.concepts, (conceptKey: ConceptKey) =>
		containsConceptByKey(concepts, conceptKey)
	);
};

export const getBonusScore = (combination: Combination): Score => {
	return { happiness: combination.bonusPoints };
};

const isNewActivatedCombination = (
	combination: Combination,
	concept: Concept,
	activeConcepts: Concepts
): boolean => {
	return (
		combinationContainsConcept(combination, concept) &&
		matchesConcepts(combination, [...activeConcepts, concept])
	);
};

export const findNewActivatedCombinations = (
	concept: Concept,
	activeConcepts: Concepts
): Combinations => {
	return filter(getCombinations(), (combination: Combination) =>
		isNewActivatedCombination(combination, concept, activeConcepts)
	);
};

export const hasNewActivatedCombinations = (
	concept: Concept,
	activeConcepts: Concepts
): boolean => {
	return some(getCombinations(), (combination: Combination) =>
		isNewActivatedCombination(combination, concept, activeConcepts)
	);
};
